import React from 'react';
import { FieldArray, Field, FieldProps, ErrorMessage, getIn, useFormikContext } from 'formik';
import { Box, Typography, Button, IconButton } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { FieldConfig } from '../../../services/ValidationService'; // Adjust path
import { TFunction, i18n } from 'i18next';
import { TGeogridProductData } from '../../../data/IUnifiedProductData'; // Adjust path
import InfoBoxButton from '../../../components/InfoBoxButton';
import { useTranslation } from 'react-i18next';
import { FormatSymbol } from '../../../enum/SubscriptSymbol';
import GeoGridFieldInput from './GeoGridFieldInput';
//import { renderSingleField } from './GeoGridForm'; // Adjust path

// Funzione helper per renderizzare singoli campi
// MODIFIED: Accept renderErrorMessageComponent as an argument
const renderSingleField = (
    fieldName: string,
    fieldConfig: FieldConfig,
    t: TFunction,
    getMeasurementUnit: Function,
    renderErrorMessageComponent: (msg: any) => React.ReactNode, // Accept the renderer
    i18n: i18n
) => {
    const showSymbol = fieldConfig.symbol && fieldConfig.renderAs !== 'select';
    // Use explicit infoBoxTitle if available, otherwise fallback to dynamic key or label
    const titleKey = fieldConfig.infoBoxTitle || `${fieldConfig.name}_info_title`;
    const dialogTitle = i18n.exists(titleKey) ? t(titleKey) : t(fieldConfig.label || '');

    return (
        <div key={fieldName} className="field-container field-container-array" style={{ marginBottom: '10px' }}>
            <label htmlFor={fieldName} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>
                    {t(fieldConfig.label || '')} {getMeasurementUnit(Array.isArray(fieldConfig.unit) ? fieldConfig.unit : undefined)}
                </span>
                {fieldConfig.infoBox && <InfoBoxButton textContent={t(fieldConfig.infoBox)} tooltipText={t(fieldConfig.infoBox)} dialogTitle={dialogTitle} size="small" />}
                {fieldConfig.infoBoxImgSrc && (
                    <InfoBoxButton
                        imageSrc={fieldConfig.infoBoxImgSrc}
                        tooltipText={t(`${fieldConfig.name}_info_tooltip`, {}) || undefined}
                        dialogTitle={t(`${fieldConfig.name}_info_title`, {}) || fieldConfig.label}
                        imageAlt={t(`${fieldConfig.name}_info_alt`, {}) || `${fieldConfig.label} information`}
                        size="small"
                    />
                )}
            </label>
            {fieldConfig.renderAs === 'select' ? (
                <Field as="select" id={fieldName} name={fieldName}>
                    {fieldConfig.options?.map(opt => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </Field>
            ) : (
                <div className={showSymbol ? 'input-container' : ''}>
                    {fieldConfig.type === 'number' ? (
                        // Positions keep their precision; the limit within the thickness is the schema's position-within-thickness test
                        <Field name={fieldName}>{({ field }: FieldProps) => <GeoGridFieldInput field={field} fieldConfig={fieldConfig} fieldName={fieldName} decimalSurface="geogridItem" />}</Field>
                    ) : (
                        <Field id={fieldName} name={fieldName} type={fieldConfig.type} className={showSymbol ? 'input-with-symbol' : ''} />
                    )}
                    {showSymbol && fieldConfig.type !== 'number' && (
                        <span className="input-symbol">
                            <FormatSymbol symbol={fieldConfig.symbol!} />
                        </span>
                    )}
                </div>
            )}
            {/* MODIFIED: Use the passed renderErrorMessageComponent */}
            <ErrorMessage name={fieldName} render={renderErrorMessageComponent} />
        </div>
    );
};

interface GeogridLayerManagerProps {
    groupName: string;
    itemFieldConfigs: FieldConfig[]; // Configurazione per i campi *dentro* ogni geogriglia
    t: TFunction;
    getMeasurementUnit: (unit: string[] | undefined) => string;
    renderErrorMessageComponent: (msg: any) => React.ReactNode;
    productData: TGeogridProductData | null;
}

const GeogridLayerManager: React.FC<GeogridLayerManagerProps> = ({
    groupName,
    itemFieldConfigs,
    t,
    getMeasurementUnit,
    renderErrorMessageComponent, // Destructure the prop
    productData,
}) => {
    const { i18n } = useTranslation();
    //console.log('[GeogridLayerManager Component] Received itemFieldConfigs:', JSON.stringify(itemFieldConfigs)); // Stampa le config degli item
    const { values } = useFormikContext<any>(); // Ottieni i valori di Formik
    const arrayName = `${groupName}.geogrids`; // Percorso dell'array nello stato Formik
    const currentGeogridItems = getIn(values, arrayName) || [];

    // Definisci l'oggetto di default per una nuova geogriglia
    const defaultNewItem: Record<string, any> = {};
    itemFieldConfigs.forEach(f => {
        defaultNewItem[f.name] = f.defaultValue ?? (f.type === 'number' ? 0 : '');
    });

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
                {t('geogrid_layers', 'Geogrid Layers')}
            </Typography>

            <FieldArray name={arrayName}>
                {({ remove, push }) => (
                    <div>
                        {currentGeogridItems.length > 0 ? (
                            currentGeogridItems.map((item: any, index: number) => (
                                <Box key={index} sx={{ border: '1px dashed #ccc', p: 2, mb: 2, position: 'relative' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>
                                        {t('geogrid_layer_title', `Geogrid ${index + 1}`)}
                                    </Typography>

                                    {/* --- LOGICA DI RENDERING CAMPI MODIFICATA --- */}
                                    {itemFieldConfigs.map(config => {
                                        const fullItemFieldName = `${arrayName}[${index}].${config.name}`;

                                        // --- Gestione Specifica per le SELECT ---
                                        if (config.renderAs === 'select') {
                                            let options: { value: string | number; label: string }[] = [];

                                            if (config.name === 'type' && productData) {
                                                // Filter families to only show those with applicable products for this project type
                                                const familiesWithProducts = Object.keys(productData).filter(family => productData[family] && productData[family].length > 0);
                                                options = familiesWithProducts.map(family => ({
                                                    value: family,
                                                    label: family,
                                                }));
                                            } else if (config.name === 'selectedGeogrid' && productData) {
                                                // Popola options per 'selectedGeogrid' in base al 'type' selezionato
                                                const selectedType = getIn(values, `${arrayName}[${index}].type`); // Leggi valore type corrente
                                                if (selectedType && productData[selectedType]) {
                                                    options = productData[selectedType].map(productInfo => ({
                                                        value: productInfo.prodotto, // Usa il nome/codice prodotto come value
                                                        label: productInfo.prodotto, // E come label
                                                    }));
                                                } else {
                                                    // Nessun tipo selezionato o tipo non valido, non mostrare opzioni prodotto
                                                    options = []; // O un'opzione placeholder
                                                }
                                            } else {
                                                // Use static options from config for other select fields
                                                options = config.options || [];
                                            }

                                            // Renderizza il componente Field select con le options dinamiche
                                            return (
                                                <div key={fullItemFieldName} className="field-container field-container-array" style={{ marginBottom: '10px' }}>
                                                    <label htmlFor={fullItemFieldName}>
                                                        {t(config.label || '')} {getMeasurementUnit(Array.isArray(config.unit) ? config.unit : undefined)}
                                                    </label>
                                                    <Field as="select" id={fullItemFieldName} name={fullItemFieldName}>
                                                        {/* Aggiungi un'opzione placeholder se il campo non è obbligatorio o come default */}
                                                        <option value="">{t('select_placeholder', '-- Select --')}</option>
                                                        {options.map(opt => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </Field>
                                                    <ErrorMessage name={fullItemFieldName}>{msg => <div className="error formik-error" /*...*/>{msg}</div>}</ErrorMessage>
                                                </div>
                                            );
                                        } else {
                                            // --- Renderizza altri campi usando l'helper ---
                                            // Pass arguments individually, matching the function signature
                                            return renderSingleField(
                                                fullItemFieldName, // fieldName
                                                config, // fieldConfig
                                                t, // t
                                                getMeasurementUnit, // getMeasurementUnit
                                                renderErrorMessageComponent, // renderErrorMessageComponent
                                                i18n
                                            );
                                        }
                                        // --- FINE LOGICA MODIFICATA ---
                                    })}
                                    <IconButton
                                        type="button"
                                        onClick={() => remove(index)}
                                        size="small"
                                        color="warning"
                                        aria-label={t('remove_layer_aria', { layer: index + 1 }) || 'Remove Layer'}
                                        sx={{ position: 'absolute', top: 8, right: 8 }}
                                        title={t('remove_layer_aria', { layer: index + 1 }) || 'Remove Layer'} // Tooltip
                                    >
                                        <RemoveCircleOutlineIcon />
                                    </IconButton>
                                </Box>
                            ))
                        ) : (
                            <Typography variant="body2" sx={{ fontStyle: 'italic', my: 1 }}>
                                {t('no_geogrid_layers_added', { context: groupName })}
                            </Typography>
                        )}
                        {/* Bottone Aggiungi */}
                        <Button type="button" onClick={() => push(defaultNewItem)} startIcon={<AddCircleOutlineIcon />} variant="outlined" size="small" sx={{ mt: 1 }}>
                            {t('add_geogrid_layer', 'Add Geogrid Layer')}
                        </Button>
                    </div>
                )}
            </FieldArray>
        </Box>
    );
};

export default GeogridLayerManager;
